import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import websocketService from '../services/websocketService';
import { playNotificationSound } from '../utils/notificationSound';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [token, setToken] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const apiUrl = process.env.REACT_APP_PETER_BOT_API_URL || 'http://localhost:5002';

  // Load chat state from sessionStorage on mount
  useEffect(() => {
    const savedConversationId = sessionStorage.getItem('chat_conversationId');
    const savedToken = sessionStorage.getItem('chat_token');
    const savedIsOpen = sessionStorage.getItem('chat_isOpen') === 'true';
    const savedIsMinimized = sessionStorage.getItem('chat_isMinimized') === 'true';
    
    if (savedConversationId && savedToken) {
      setConversationId(savedConversationId);
      setToken(savedToken);
      if (savedIsOpen) {
        setIsOpen(true);
        setIsMinimized(savedIsMinimized);
      }
    }
  }, []);

  // Check for active conversation when user is available
  useEffect(() => {
    if (!user?.id) return;

    const checkActiveConversation = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/conversations/active?userId=${user.id}`);
        if (!response.ok) {
          console.error('[ChatContext] Failed to fetch active conversation');
          return;
        }

        const data = await response.json();
        if (data && data.id) {
          // We have an active conversation
          const existingConvId = sessionStorage.getItem('chat_conversationId');
          
          // Always update conversationId and token to ensure we have the latest token
          // (tokens can expire or be regenerated)
          if (data.chat_token) {
            setConversationId(data.id);
            setToken(data.chat_token);
            
            // Update unread count
            if (data.unreadCount !== undefined) {
              setUnreadCount(data.unreadCount);
            }
            
            // Don't auto-open, just show notification badge if there are unread messages
            if (data.unreadCount > 0) {
              console.log('[ChatContext] Active conversation found with unread messages:', data.unreadCount);
            }
          } else {
            console.warn('[ChatContext] Active conversation found but no chat_token provided');
          }
        }
      } catch (error) {
        console.error('[ChatContext] Error checking active conversation:', error);
      }
    };

    checkActiveConversation();
  }, [user?.id, apiUrl]);

  // WebSocket connection for real-time unread count updates
  useEffect(() => {
    if (!conversationId || !token) {
      // Disconnect if no conversation
      websocketService.disconnect();
      return;
    }

    // Connect to WebSocket (if not already connected)
    console.log('[ChatContext] Establishing WebSocket connection:', { conversationId, userId: user?.id, isOpen });
    websocketService.connect(conversationId, token, user?.id);
    
    // Listen for authentication completion
    const unsubscribeConnected = websocketService.on('connected', (data) => {
      if (data.conversationId === conversationId) {
        console.log('[ChatContext] WebSocket authenticated and ready:', { conversationId, isOpen });
      }
    });
    
    // Verify connection status after a short delay
    setTimeout(() => {
      const isConnected = websocketService.getConnectionStatus();
      console.log('[ChatContext] WebSocket connection status:', { isConnected, conversationId, isOpen });
    }, 1000);

    // Listen for new messages to update unread count
    // Accept events for current conversation OR any conversation for this user (from user room)
    const unsubscribeMessage = websocketService.on('message:new', (data) => {
      // Accept if it's for the current conversation OR if we have a userId (events from user room)
      const isCurrentConversation = data.conversationId === conversationId;
      const isUserRoomEvent = user?.id && data.conversationId; // Accept all events from user room
      
      if (isCurrentConversation || isUserRoomEvent) {
        console.log('[ChatContext] Received message:new event:', { 
          hasMessage: !!data.message, 
          messageRole: data.message?.role,
          unreadCount: data.unreadCount,
          isOpen,
          conversationId: data.conversationId,
          isCurrentConversation,
          isUserRoomEvent
        });
        
        // If conversation ID changed, fetch the new active conversation to get the token
        if (isUserRoomEvent && data.conversationId !== conversationId) {
          console.log('[ChatContext] Active conversation changed, fetching new conversation:', {
            old: conversationId,
            new: data.conversationId
          });
          // Fetch the new active conversation to get the token
          fetch(`${apiUrl}/api/conversations/active?userId=${user.id}`)
            .then(res => res.json())
            .then(newConv => {
              if (newConv && newConv.id && newConv.chat_token) {
                setConversationId(newConv.id);
                setToken(newConv.chat_token);
                if (newConv.unreadCount !== undefined) {
                  setUnreadCount(newConv.unreadCount);
                }
                // Re-authenticate with new conversation ID and token
                websocketService.connect(newConv.id, newConv.chat_token, user?.id);
              }
            })
            .catch(err => console.error('[ChatContext] Error fetching new active conversation:', err));
        }
        
        // If it's a bot message and chat is closed, increment local unread count and play sound
        if (!isOpen && data.message && data.message.role === 'bot') {
          console.log('[ChatContext] Incrementing unread count (bot message received)');
          // Play notification sound for new bot message
          playNotificationSound();
          setUnreadCount(prev => {
            const newCount = prev + 1;
            console.log('[ChatContext] Unread count updated:', { prev, newCount });
            return newCount;
          });
        }
        
        // Also update from server's calculation if provided (for accuracy)
        // Don't play sound here to avoid duplicates - sound already played above for bot messages
        if (!isOpen && data.unreadCount !== undefined && (!data.message || data.message.role !== 'bot')) {
          console.log('[ChatContext] Updating unread count from server:', data.unreadCount);
          setUnreadCount(prev => {
            // Only update if we didn't already handle it above (non-bot messages or count-only updates)
            return data.unreadCount;
          });
        }
      }
    });

    // Listen for conversation updates
    const unsubscribeUpdate = websocketService.on('conversation:updated', (data) => {
      if (data.conversationId === conversationId) {
        console.log('[ChatContext] Received conversation:updated event:', { 
          unreadCount: data.unreadCount,
          isOpen 
        });
        
        // Update unread count if chat is closed
        // Play sound if unread count increased (new notification)
        if (!isOpen && data.unreadCount !== undefined) {
          console.log('[ChatContext] Updating unread count from conversation update:', data.unreadCount);
          setUnreadCount(prev => {
            // Play sound if this is a new notification (count increased)
            if (data.unreadCount > prev) {
              playNotificationSound();
            }
            return data.unreadCount;
          });
        }
      }
    });

    // Cleanup
    return () => {
      unsubscribeConnected();
      unsubscribeMessage();
      unsubscribeUpdate();
      // Don't disconnect here - keep connection alive for real-time updates
    };
  }, [conversationId, token, isOpen]);

  // Save chat state to sessionStorage
  useEffect(() => {
    if (conversationId) {
      sessionStorage.setItem('chat_conversationId', conversationId);
    } else {
      sessionStorage.removeItem('chat_conversationId');
    }
  }, [conversationId]);

  useEffect(() => {
    if (token) {
      sessionStorage.setItem('chat_token', token);
    } else {
      sessionStorage.removeItem('chat_token');
    }
  }, [token]);

  useEffect(() => {
    sessionStorage.setItem('chat_isOpen', isOpen.toString());
  }, [isOpen]);

  useEffect(() => {
    sessionStorage.setItem('chat_isMinimized', isMinimized.toString());
  }, [isMinimized]);

  const openChat = useCallback(async (convId, chatToken) => {
    if (convId && chatToken) {
      console.log('[ChatContext] Opening chat:', { convId, hasToken: !!chatToken });
      
      // If token might be expired, refresh it by fetching active conversation
      try {
        const response = await fetch(`${apiUrl}/api/conversations/active?userId=${user?.id}`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.id === convId && data.chat_token) {
            // Use the fresh token from the API
            setToken(data.chat_token);
            chatToken = data.chat_token; // Update local variable for subsequent calls
            console.log('[ChatContext] Refreshed token from active conversation API');
          }
        }
      } catch (error) {
        console.warn('[ChatContext] Could not refresh token, using provided token:', error);
      }
      
      setConversationId(convId);
      setToken(chatToken);
      setIsOpen(true);
      setIsMinimized(false);
      setUnreadCount(0);
      
      // Mark conversation as read when opening
      try {
        await fetch(`${apiUrl}/api/chat/${convId}/read?token=${chatToken}`, {
          method: 'POST'
        });
      } catch (error) {
        console.error('[ChatContext] Error marking conversation as read:', error);
      }
      
      console.log('[ChatContext] Chat state updated - should be open now');
    } else {
      console.warn('[ChatContext] openChat called with invalid params:', { convId, hasToken: !!chatToken });
    }
  }, [apiUrl, user?.id]);

  const closeChat = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
    // Optionally clear conversation data
    // setConversationId(null);
    // setToken(null);
  }, []);

  const minimizeChat = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const maximizeChat = useCallback(() => {
    setIsMinimized(false);
    setIsOpen(true);
  }, []);

  const toggleChat = useCallback(() => {
    if (isOpen && !isMinimized) {
      minimizeChat();
    } else if (isOpen && isMinimized) {
      maximizeChat();
    } else if (conversationId && token) {
      openChat(conversationId, token);
    }
  }, [isOpen, isMinimized, conversationId, token, openChat, minimizeChat, maximizeChat]);

  const incrementUnreadCount = useCallback(() => {
    setUnreadCount(prev => prev + 1);
  }, []);

  const clearUnreadCount = useCallback(async () => {
    setUnreadCount(0);
    // Mark conversation as read
    if (conversationId && token) {
      try {
        await fetch(`${apiUrl}/api/chat/${conversationId}/read?token=${token}`, {
          method: 'POST'
        });
      } catch (error) {
        console.error('[ChatContext] Error marking conversation as read:', error);
      }
    }
  }, [conversationId, token, apiUrl]);

  const value = {
    isOpen,
    isMinimized,
    conversationId,
    token,
    unreadCount,
    openChat,
    closeChat,
    minimizeChat,
    maximizeChat,
    toggleChat,
    incrementUnreadCount,
    clearUnreadCount,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

